<template>
  <div class="relative h-full w-full overflow-hidden">
    <PanelFilters
      v-if="filters?.length > 0"
      v-model="filters"
      class="absolute bottom-0 top-0 z-10 transition-all duration-300 ease-in-out"
      :class="!showFilters ? '-right-[375px]' : 'right-0'"
      @close="closeFilters"
    />
    <ag-grid-vue
      class="ag-theme-alpine"
      :class="[$attrs.class, { normal: isNormal, hidden: loading }, className]"
      :column-defs="columnDefs"
      :row-data="filteredData"
      :pagination="pagination"
      :header-height="64"
      :row-height="rowHeight"
      :default-col-def="defaultColDef"
      :dom-layout="domLayout"
      :pagination-page-size="rowPerPage"
      suppress-pagination-panel
      enable-sorting
      animate-rows
      suppress-scroll-on-new-data
      @grid-ready="onGridReady"
      @pagination-changed="onPaginationChanged"
      @cell-clicked="$emit('cellClick', $event)"
      @first-data-rendered="onFirstDataRendered"
      @grid-size-changed="onGridSizeChanged"
      @sort-changed="onSortChanged"
      @body-scroll="onBodyScroll"
    />
    <PaginationCustom
      v-if="pagination && !loading"
      :pages="pages"
      :current-page="currentPage"
      :row-per-page="rowPerPage"
      :rows-per-page-list="rowsPerPageList"
      :total="total"
      @change-row-per-page="onChangeRowPerPage"
      @change-page="onChangePage"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeMount } from 'vue'
import { AgGridVue } from 'ag-grid-vue3'
import { createFilters, filterDataWithPanel } from '~/utils/filters'

import PanelFilters from '~/components/filters/Panel.vue'
import PaginationCustom from '~/components/price-assessments/PaginationTableAG.vue'

const props = withDefaults(
  defineProps<{
    columnDefs: Array<Record<string, unknown>>
    rowData: Array<Record<string, unknown>>
    pagination?: boolean
    rowHeight?: number
    rowsPerPageList?: number[]
    defaultColDef?: Record<string, unknown>
    layout?: string
    isFixed?: boolean
    className?: string
    showFilters?: boolean
    filterOptions?: Record<string, unknown>
  }>(),
  {
    pagination: false,
    rowHeight: 64,
    rowsPerPageList: () => [10, 50, 100],
    defaultColDef: () => ({
      sortable: true,
      resizable: false,
      suppressMovable: true,
      suppressMenu: true,
      unSortIcon: true,
    }),
    className: '',
    showFilters: false,
    filterOptions: () => ({}),
    isFixed: undefined,
  },
)

const emit = defineEmits(['closeFilters', 'cellClick', 'bodyScroll'])
const filters = ref<Array<Record<string, unknown>>>([])
const gridApi = ref<any>(null)
const rowPerPage = ref<number>(props.rowsPerPageList[0])
const loading = ref<boolean>(true)
const currentPage = ref<number>(1)
const pages = ref<number>(1)

const filteredData = computed(() =>
  filterDataWithPanel(props.rowData, filters.value),
)
const isNormal = computed(() => {
  if (props.isFixed !== undefined) return props.isFixed
  return rowsInPage.value > 10
})
const rowsInPage = computed(() => {
  if (!gridApi.value || !currentPage.value || !rowPerPage.value) return 0
  const totalRows = gridApi.value.paginationGetRowCount()
  if (rowPerPage.value === totalRows) return rowPerPage.value
  if (currentPage.value === pages.value) {
    return totalRows % rowPerPage.value
  }
  return rowPerPage.value
})
const domLayout = computed(() => {
  if (props.layout !== undefined) return props.layout
  return isNormal.value ? 'normal' : 'autoHeight'
})
const total = computed(() => props.rowData.length || 0)

watch(
  () => props.rowData,
  (data) => {
    if (!data) return
    filters.value = createFilters({
      data,
      filterOptions: props.filterOptions,
    })
  },
  { immediate: true, deep: true },
)

onBeforeMount(() => {
  if (props.pagination) {
    rowPerPage.value = props.rowsPerPageList[0]
  }
})

const closeFilters = () => {
  emit('closeFilters')
}

const onGridReady = (params: any) => {
  loading.value = true
  gridApi.value = params.api
  if (props.pagination) {
    gridApi.value.setGridOption('pagination', true)
    gridApi.value.setGridOption('paginationPageSize', rowPerPage.value)
  }
  gridApi.value.setGridOption('rowData', props.rowData)
  loading.value = false
}

const onFirstDataRendered = () => {
  if (!gridApi.value) return
  gridApi.value.sizeColumnsToFit()
}

const onGridSizeChanged = () => {
  if (!gridApi.value) return
  gridApi.value.sizeColumnsToFit()
}

const onBodyScroll = (event: any) => {
  onVerticalScroll(event)
  onHorizontalScroll(event)
  emit('bodyScroll', event)
}

const onVerticalScroll = (event: any) => {
  const elements = document.querySelectorAll(
    '.ag-theme-alpine .ag-pinned-left-cols-container, .ag-theme-alpine .ag-pinned-left-header',
  )
  elements.forEach((el) => {
    el.style.boxShadow =
      event.left > 0 ? '6px 0 4px 0 rgba(0, 0, 0, 0.25)' : 'none'
  })
}

const onHorizontalScroll = (event: any) => {
  const elements = document.querySelectorAll('.ag-theme-alpine .ag-header')
  elements.forEach((el) => {
    el.style.boxShadow =
      event.top > 0 ? '0 6px 4px 0 rgba(0, 0, 0, 0.25)' : 'none'
  })
}

const onPaginationChanged = () => {
  if (!gridApi.value) return
  gridApi.value.sizeColumnsToFit()
  currentPage.value = gridApi.value.paginationGetCurrentPage() + 1
  rowPerPage.value = gridApi.value.paginationGetPageSize()
  pages.value = gridApi.value.paginationGetTotalPages()
}

const onChangePage = (page: number) => {
  if (!gridApi.value) return
  gridApi.value.paginationGoToPage(page - 1)
}

const onChangeRowPerPage = (value: number) => {
  if (!gridApi.value) return
  rowPerPage.value = value
  gridApi.value.setGridOption('paginationPageSize', value)
  onChangePage(1)
}

const onSortChanged = () => {
  onChangePage(1)
}
</script>
